import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { PublicPreboardingFormSection } from '@hygge/shared';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { PublicFieldRenderer } from './field-renderer';

interface SectionStepperProps {
  section: PublicPreboardingFormSection;
  sectionNumber: number; // 1-based
  totalSections: number;
  isLast: boolean;
  answers: Record<string, string | boolean>;
  files: Record<string, File | null>;
  onChange: (fieldId: string, value: string | boolean) => void;
  onFileChange: (fieldId: string, file: File | null) => void;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  gdprConsent: boolean;
  onGdprChange: (value: boolean) => void;
  submitting: boolean;
  submitError: string | null;
}

export function SectionStepper({
  section,
  sectionNumber,
  totalSections,
  isLast,
  answers,
  files,
  onChange,
  onFileChange,
  onBack,
  onNext,
  onSubmit,
  gdprConsent,
  onGdprChange,
  submitting,
  submitError,
}: SectionStepperProps) {
  const sectionValid = section.fields.every((field) =>
    isFieldFilled(field, answers[field.id], files[field.id]),
  );
  const canSubmit = isLast && sectionValid && gdprConsent;

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-10">
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Section {sectionNumber} of {totalSections}
        </p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">{section.title}</h1>
        {section.description && (
          <p className="mt-1 text-sm text-muted-foreground">{section.description}</p>
        )}
      </div>

      <div className="space-y-5">
        {section.fields.map((field) => (
          <PublicFieldRenderer
            key={field.id}
            field={field}
            value={answers[field.id] ?? (field.inputType === 'CHECKBOX' ? false : '')}
            onChange={(v) => onChange(field.id, v)}
            file={field.inputType === 'FILE' ? (files[field.id] ?? null) : undefined}
            onFileChange={
              field.inputType === 'FILE' ? (f) => onFileChange(field.id, f) : undefined
            }
          />
        ))}
      </div>

      {isLast && (
        <div className="mt-6 flex items-start gap-2.5 border-t border-border pt-5">
          <Checkbox
            id="preboarding-gdpr"
            checked={gdprConsent}
            onCheckedChange={(c) => onGdprChange(c === true)}
            className="mt-0.5"
          />
          <label
            htmlFor="preboarding-gdpr"
            className="text-[13px] leading-relaxed text-muted-foreground cursor-pointer"
          >
            I consent to the processing of my personal data in accordance with the{' '}
            <a
              href="https://hygge.software/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-hygge underline underline-offset-2"
            >
              Privacy Policy
            </a>{' '}
            *
          </label>
        </div>
      )}

      {isLast && submitError && (
        <div className="mt-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
          {submitError}
        </div>
      )}

      <div className="mt-7 flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={submitting}
          className="gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        {isLast ? (
          <Button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit || submitting}
            className="bg-cta hover:bg-cta/90 text-cta-foreground font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={onNext}
            disabled={!sectionValid}
            className="gap-1.5 bg-cta hover:bg-cta/90 text-cta-foreground font-semibold"
          >
            Next
            <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// A required field must carry a value appropriate to its input type. Optional
// fields are always considered satisfied. NUMBER additionally must be >= 0.
function isFieldFilled(
  field: PublicPreboardingFormSection['fields'][number],
  value: string | boolean | undefined,
  file: File | null | undefined,
): boolean {
  if (field.inputType === 'NUMBER' && typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return false;
  }
  if (!field.required) return true;
  if (field.inputType === 'FILE') return !!file;
  if (field.inputType === 'CHECKBOX') return value === true;
  return typeof value === 'string' && value.trim() !== '';
}
